import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useState } from 'react'
import { Funcionario, funcionario as api } from 'service/api'
import { PaginatedResponse } from 'service/api/types'
import { toast } from 'sonner'

dayjs.extend(utc)

/** Recorte da listagem de funcionários que a exportação reproduz. */
export interface FiltrosExportacao {
    search?: string
    filter?: string
    centroCustoIds?: number[]
    locaisIds?: number[]
}

/** Maior página aceita pela listagem de funcionários. */
const TAMANHO_PAGINA = 100

/** Busca todas as páginas da listagem de funcionários com o recorte da tela. */
export async function buscarFuncionariosFiltrados(
    empresaId: number,
    { search, filter, centroCustoIds, locaisIds }: FiltrosExportacao,
): Promise<Funcionario[]> {
    const funcionarios: Funcionario[] = []

    for (let page = 1; ; page++) {
        const { data, meta } = await api.get<PaginatedResponse<Funcionario>>({
            empresaId,
            page,
            perPage: TAMANHO_PAGINA,
            ...(search && { search }),
            ...(filter && { filter }),
            ...(centroCustoIds?.length && { centroCustoIds }),
            ...(locaisIds?.length && { locaisIds }),
        } as any)
        funcionarios.push(...data)

        // Página vazia encerra mesmo com `total` maior: o cadastro pode mudar durante a exportação.
        if (!data.length || funcionarios.length >= meta.total)
            return funcionarios
    }
}

/** Monta a planilha no layout aceito pela importação de funcionários: cabeçalho e uma linha por funcionário. */
export function linhasImportacao(...funcionarios: Funcionario[]) {
    return [
        [
            'Matrícula',
            'Nome',
            'Cargo/Função',
            'PIS/PASEP',
            'CPF',
            'Data de admissão',
        ],
        ...funcionarios.map(funcionario => [
            funcionario.matricula,
            funcionario.nome,
            funcionario.funcao ?? '',
            funcionario.pis ?? '',
            funcionario.cpf ?? '',
            funcionario.dtAdmissao
                ? dayjs.utc(funcionario.dtAdmissao).format('DD/MM/YYYY')
                : '',
        ]),
    ]
}

async function baixarPlanilhaImportacao(funcionario: Funcionario) {
    const XLSX = await import('xlsx')
    const planilha = XLSX.utils.aoa_to_sheet(linhasImportacao(funcionario))
    planilha['!cols'] = [
        { wpx: 60 },
        { wpx: 200 },
        { wpx: 75 },
        { wpx: 80 },
        { wpx: 80 },
        { wpx: 100 },
    ]

    const pasta = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(pasta, planilha, 'Funcionários')
    XLSX.writeFile(pasta, 'ficha-funcionario.xlsx')
}

export function ExportarService() {
    const [isCarregando, setIsCarregando] = useState<boolean>(false)

    /** Busca o cadastro atual do funcionário e baixa a ficha em pdf ou na planilha de importação. */
    async function handleExportarFicha(
        funcionarioId: number,
        tipo: 'pdf' | 'xls',
    ) {
        if (funcionarioId <= 0) return
        setIsCarregando(true)

        try {
            const funcionario = await api.getById(funcionarioId)

            if (tipo === 'pdf') {
                await api.ficha.pdf({ data: funcionario })
            } else {
                await baixarPlanilhaImportacao(funcionario)
            }
        } catch (error) {
            toast.error('Erro ao exportar a ficha do funcionário.')
            console.error('[Exportar] falha ao gerar ficha:', error)
        } finally {
            setIsCarregando(false)
        }
    }

    return {
        handleExportarFicha,
        isCarregando,
    }
}
